import { useState, useEffect, useCallback } from 'react';
import { getProfiles, updateProfile } from '../api/pocketbase';
import ProfileCard from './ProfileCard';
import { getDatePart, toPocketBaseDateTime } from '../utils/dateFields';

const getDateStr = (p) => getDatePart(p.date_reserver);
const getDateTimeSortValue = (value) => {
  if (!value) return null;

  const time = Date.parse(String(value).trim().replace(' ', 'T'));
  return Number.isNaN(time) ? null : time;
};
const getTodayStr = () => {
  const date = new Date();
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().split('T')[0];
};
const idPcEmpty = (p) => !p.id_pc || p.id_pc.trim() === '' || p.id_pc === '-' || p.id_pc.toLowerCase() === 'no dispo';

function ProfileList({ refreshKey }) {
  const [profiles, setProfiles] = useState([]);
  const [search, setSearch] = useState('');
  const [pcFilter, setPcFilter] = useState('');
  const [dateFilterField, setDateFilterField] = useState('created');
  const [dateFilterValue, setDateFilterValue] = useState('');
  const [dateTakenSort, setDateTakenSort] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkingAll, setCheckingAll] = useState(false);
  const [checkAllProgress, setCheckAllProgress] = useState({ current: 0, total: 0, email: '' });
  const [checkAllResult, setCheckAllResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getProfiles();
      const today = getTodayStr();
      const syncedData = await Promise.all(data.map(async (profile) => {
        const reservedDate = getDateStr(profile);
        if (reservedDate && reservedDate >= today && !idPcEmpty(profile)) {
          try {
            await updateProfile(profile.id, { id_pc: '-' });
            return { ...profile, id_pc: '-' };
          } catch (_) {
            return profile;
          }
        }

        return profile;
      }));
      setProfiles(syncedData);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const todayStr = getTodayStr();
  const canCheckAppointment = (p) => p.email && p.app_password && p.app_password !== '-';

  const handleCheckAll = async () => {
    const total = profiles.length;

    if (total === 0) return;

    setCheckingAll(true);
    setCheckAllResult(null);
    setCheckAllProgress({ current: 0, total, email: '' });

    const result = {
      updated: 0,
      notFound: 0,
      skipped: 0,
      failed: 0,
    };

    for (let index = 0; index < profiles.length; index += 1) {
      const profile = profiles[index];

      setCheckAllProgress({
        current: index + 1,
        total,
        email: profile.email || 'No email',
      });

      if (!canCheckAppointment(profile)) {
        result.skipped += 1;
        continue;
      }

      try {
        const response = await fetch('/api/check-appointment', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: profile.email, app_password: profile.app_password }),
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Check failed');

        const updates = {};
        if (data.appointmentDate && data.appointmentDate !== 'Not found in PDF') {
          const appointmentDate = data.appointmentDate.split(' ')[0];
          updates.date_reserver = appointmentDate;
          if (appointmentDate >= todayStr) updates.id_pc = '-';
        }
        if (data.dateTaken && data.dateTaken !== 'Not found in PDF') {
          updates.date_taken = toPocketBaseDateTime(data.dateTaken);
        }

        if (Object.keys(updates).length > 0) {
          await updateProfile(profile.id, updates);
          result.updated += 1;
        } else {
          result.notFound += 1;
        }
      } catch (_) {
        result.failed += 1;
      }
    }

    setCheckAllResult(result);
    setCheckingAll(false);
    setCheckAllProgress({ current: 0, total: 0, email: '' });
    load();
  };

  const pcNames = [...new Set(
    profiles
      .map((p) => (p.id_pc || '').trim())
      .filter((v) => v && v !== '-' && v.toLowerCase() !== 'no dispo')
      .map((v) => {
        const lower = v.toLowerCase();
        const name = lower.replace(/[^a-z]/g, '').trim();
        return { original: v, name };
      })
      .reduce((acc, cur) => {
        if (!acc.find((a) => a.name === cur.name)) acc.push(cur);
        return acc;
      }, [])
      .map((v) => v.name)
      .sort((a, b) => a.localeCompare(b))
  )];

  const filtered = profiles.filter((p) => {
    if (pcFilter) {
      const pName = (p.id_pc || '').toLowerCase().replace(/[^a-z]/g, '').trim();
      if (pName !== pcFilter) return false;
    }
    if (dateFilterValue) {
      if (getDatePart(p[dateFilterField]) !== dateFilterValue) return false;
    }
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (p.email || '').toLowerCase().includes(q) ||
      (p.cnn || '').toLowerCase().includes(q) ||
      (p.id_pc || '').toLowerCase().includes(q) ||
      (p.phone_number || '').toLowerCase().includes(q) ||
      (p.date_taken || '').toLowerCase().includes(q)
    );
  });

  const sorted = [...filtered].sort((a, b) => {
    const aTime = getDateTimeSortValue(a.date_taken);
    const bTime = getDateTimeSortValue(b.date_taken);

    if (aTime === null && bTime === null) return 0;
    if (aTime === null) return 1;
    if (bTime === null) return -1;

    return dateTakenSort === 'asc' ? aTime - bTime : bTime - aTime;
  });

  const stats = {
    reserved: profiles.filter((p) => { const d = getDateStr(p); return d && d >= todayStr; }).length,
    dispo: profiles.filter((p) => { const d = getDateStr(p); return d && d < todayStr && idPcEmpty(p); }).length,
    active: profiles.filter((p) => { const d = getDateStr(p); return d && d < todayStr && !idPcEmpty(p); }).length,
  };

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className="border border-amber-500/20 bg-amber-500/[0.03] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-2 h-2 rounded-full bg-amber-400" />
            <span className="text-[11px] font-medium text-amber-400 uppercase tracking-wider">Reserved</span>
          </div>
          <div className="text-2xl font-semibold text-white tracking-tight">{stats.reserved}</div>
          <div className="text-[11px] text-zinc-500 mt-1">Date &gt; now</div>
        </div>
        <div className="border border-emerald-500/20 bg-emerald-500/[0.03] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400" />
            <span className="text-[11px] font-medium text-emerald-400 uppercase tracking-wider">Disponible</span>
          </div>
          <div className="text-2xl font-semibold text-white tracking-tight">{stats.dispo}</div>
          <div className="text-[11px] text-zinc-500 mt-1">Date &lt;= now + no PC</div>
        </div>
        <div className="border border-purple-500/20 bg-purple-500/[0.03] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-2 h-2 rounded-full bg-purple-400" />
            <span className="text-[11px] font-medium text-purple-400 uppercase tracking-wider">Active</span>
          </div>
          <div className="text-2xl font-semibold text-white tracking-tight">{stats.active}</div>
          <div className="text-[11px] text-zinc-500 mt-1">Date &lt;= now + has PC</div>
        </div>
      </div>

      {profiles.length > 0 && (
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-3">
            <svg className="text-zinc-600 flex-shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>
            </svg>
            <span className="text-[11px] text-zinc-500 font-medium uppercase tracking-wider">Filter by PC</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setPcFilter('')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-150 ${
                !pcFilter
                  ? 'bg-white/[0.1] text-white border border-white/20'
                  : 'bg-white/[0.03] text-zinc-500 border border-white/[0.06] hover:text-zinc-300 hover:border-white/12'
              }`}
            >
              All
            </button>
            {pcNames.map((name) => (
              <button
                key={name}
                onClick={() => setPcFilter(name)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-150 capitalize ${
                  pcFilter === name
                    ? 'bg-white/[0.1] text-white border border-white/20'
                    : 'bg-white/[0.03] text-zinc-500 border border-white/[0.06] hover:text-zinc-300 hover:border-white/12'
                }`}
              >
                {name}
              </button>
            ))}
          </div>
        </div>
      )}

      {profiles.length > 0 && (
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-3">
            <svg className="text-zinc-600 flex-shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>
            </svg>
            <span className="text-[11px] text-zinc-500 font-medium uppercase tracking-wider">Filter by Date</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr_auto] gap-2">
            <select
              value={dateFilterField}
              onChange={(e) => setDateFilterField(e.target.value)}
              className="px-3 py-2 rounded-lg border border-white/[0.08] bg-[#111114] text-xs text-zinc-300 outline-none focus:border-white/20 hover:border-white/12 transition-all duration-150"
            >
              <option value="created">Created</option>
              <option value="date_reserver">Reserved</option>
              <option value="date_taken">Date Taken</option>
            </select>
            <input
              type="date"
              value={dateFilterValue}
              onChange={(e) => setDateFilterValue(e.target.value)}
              className="px-3 py-2 rounded-lg border border-white/[0.08] bg-white/[0.03] text-xs text-zinc-300 outline-none focus:border-white/20 hover:border-white/12 transition-all duration-150"
            />
            {dateFilterValue && (
              <button
                onClick={() => setDateFilterValue('')}
                className="px-3 py-2 rounded-lg bg-white/[0.03] text-zinc-500 border border-white/[0.06] text-xs font-medium hover:text-zinc-300 hover:border-white/12 transition-all duration-150"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {profiles.length > 0 && (
        <div className="relative mb-5">
          <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-600" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search profiles..."
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-white/[0.08] bg-white/[0.03] text-sm text-zinc-100 placeholder-zinc-600 outline-none focus:border-white/20 hover:border-white/12 transition-all duration-150"
          />
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <span className="text-xs text-zinc-500 font-medium">{sorted.length} of {profiles.length} {profiles.length === 1 ? 'profile' : 'profiles'}</span>
        {profiles.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setDateTakenSort((value) => value === 'desc' ? 'asc' : 'desc')}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.08] text-xs font-medium text-zinc-400 hover:bg-white/[0.06] hover:text-zinc-200 hover:border-white/15 active:scale-[0.98] transition-all"
              title="Sort by Date Taken"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 6h18M7 12h10M10 18h4"/>
              </svg>
              Date Taken {dateTakenSort === 'desc' ? 'Newest' : 'Oldest'}
            </button>
            <button
              onClick={handleCheckAll}
              disabled={checkingAll || loading}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/20 text-xs font-medium text-blue-300 hover:bg-blue-500/15 hover:border-blue-500/30 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              title="Check Appointment for all profiles"
            >
              {checkingAll ? (
                <div className="w-[14px] h-[14px] border-2 border-blue-900 border-t-blue-300 rounded-full animate-spin" />
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="M21 21l-4.35-4.35"/>
                </svg>
              )}
              {checkingAll ? `${checkAllProgress.current}/${checkAllProgress.total}` : 'Check All Appointments'}
            </button>
          </div>
        )}
      </div>

      {checkingAll && (
        <div className="mb-4 px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/20 text-xs text-blue-300">
          Checking {checkAllProgress.email}
        </div>
      )}

      {checkAllResult && (
        <div className="mb-4 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.08] text-xs text-zinc-400">
          Updated {checkAllResult.updated} | Not found {checkAllResult.notFound} | Skipped {checkAllResult.skipped} | Failed {checkAllResult.failed}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-5 h-5 border-2 border-zinc-700 border-t-zinc-400 rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="border border-white/[0.06] rounded-xl p-10 text-center">
          <div className="w-10 h-10 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto mb-3">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><path d="M15 9l-6 6M9 9l6 6"/>
            </svg>
          </div>
          <p className="text-sm font-medium text-zinc-300">Failed to load</p>
          <p className="text-xs text-zinc-600 mt-1">{error}</p>
        </div>
      ) : sorted.length === 0 ? (
        <div className="border border-white/[0.06] rounded-xl p-10 text-center">
          <div className="w-10 h-10 rounded-lg bg-white/[0.04] border border-white/[0.06] flex items-center justify-center mx-auto mb-3">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#71717a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
            </svg>
          </div>
          <p className="text-sm font-medium text-zinc-300">{profiles.length === 0 ? 'No profiles yet' : 'No results found'}</p>
          <p className="text-xs text-zinc-600 mt-1">{profiles.length === 0 ? 'Create your first profile to get started' : 'Try a different search term'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sorted.map((p) => (
            <ProfileCard key={p.id} profile={p} onUpdated={load} />
          ))}
        </div>
      )}
    </div>
  );
}

export default ProfileList;
